import React, { useState, useEffect } from 'react';
import axios from 'axios';

const EMPTY_USER = { name: '', email: '', phone: '', role: 'sales', password: '' };

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full system access', color: '#ef4444', bg: '#fef2f2' },
  { value: 'director', label: 'Director', desc: 'Strategic oversight and approvals', color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'sales_head', label: 'Sales Head', desc: 'Sales team management and strategy', color: '#2563eb', bg: '#eff6ff' },
  { value: 'sales', label: 'Sales Executive', desc: 'Client and inventory management', color: '#16a34a', bg: '#f0fdf4' },
  { value: 'sales_coordinator', label: 'Sales Coordinator', desc: 'Sales coordination and support', color: '#0891b2', bg: '#ecfeff' },
  { value: 'sourcing', label: 'Sourcing', desc: 'Channel partner management only', color: '#d97706', bg: '#fffbeb' },
  { value: 'receptionist', label: 'Receptionist', desc: 'Walk-ins and basic client access', color: '#64748b', bg: '#f8fafc' }
];

const Admin = ({ apiBase }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_USER });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [serverStatus, setServerStatus] = useState(null);
  const [reveals, setReveals] = useState({ events: [], by_user: [] });

  useEffect(() => { fetchUsers(); fetchReveals(); }, []);

  const fetchReveals = async () => {
    try {
      const res = await axios.get(`${apiBase}/comms/activity/reveals`);
      setReveals(res.data || { events: [], by_user: [] });
    } catch (e) { /* tracker is additive; never block the Admin screen */ }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/users`);
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewUser = () => {
    setEditingUser(null);
    setFormData({ ...EMPTY_USER });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name || '', email: user.email || '', phone: user.phone || '', role: user.role || 'sales', password: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.put(`${apiBase}/admin/users/${editingUser.id}`, payload);
      } else {
        if (!formData.password) { alert('Password is required for new users'); return; }
        await axios.post(`${apiBase}/admin/users`, formData);
      }
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${apiBase}/admin/users/${user.id}`);
      fetchUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRevoke = async (user) => {
    const action = user.status === 'revoked' ? 'activate' : 'revoke';
    const msg = action === 'revoke' ? `Revoke access for "${user.name}"?` : `Reactivate "${user.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${apiBase}/admin/users/${user.id}/${action}`);
      fetchUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const openResetPassword = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
    try {
      await axios.post(`${apiBase}/admin/users/${passwordUser.id}/reset-password`, { newPassword });
      setShowPasswordModal(false);
      alert('Password reset successfully');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchServerStatus = async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/emergency/server-status`);
      setServerStatus(res.data);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[3];

  if (loading) return <div className="loading">Loading admin panel...</div>;

  const REVEAL_LABEL = { client: 'Client', walkin: 'Walk-in', broker: 'Channel partner' };
  const fmtWhen = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString('en-IN',
        { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return String(ts); }
  };

  const RevealTracker = () => (
    <div style={{ marginTop: '28px' }}>
      <h2 style={{ fontSize: '18px', margin: '0 0 4px 0' }}>Contact reveals</h2>
      <p style={{ margin: '0 0 14px 0', color: '#64748b', fontSize: '13px' }}>
        Phone numbers and names are hidden until a team member reveals them. A reveal
        is permanent for that person and is recorded here.
      </p>

      {reveals.by_user.length === 0 && reveals.events.length === 0 ? (
        <div style={{ padding: '14px', background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: '8px', color: '#64748b', fontSize: '13px' }}>
          No contact has been revealed yet.
        </div>
      ) : (
        <>
          {reveals.by_user.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
              {reveals.by_user.map(u => (
                <div key={u.user_id} style={{ padding: '10px 14px', background: '#fff',
                     border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '170px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{u.user_name || `User ${u.user_id}`}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{u.role || '-'}</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#0891b2', marginTop: '4px' }}>
                    {u.unlocked}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    unlocked · {u.phones} phone{Number(u.phones) === 1 ? '' : 's'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                    last {fmtWhen(u.last_reveal)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>When</th>
                  <th style={{ padding: '8px 12px' }}>Who</th>
                  <th style={{ padding: '8px 12px' }}>Revealed</th>
                  <th style={{ padding: '8px 12px' }}>Of</th>
                  <th style={{ padding: '8px 12px' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {reveals.events.map(e => (
                  <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtWhen(e.created_at)}</td>
                    <td style={{ padding: '8px 12px' }}>{e.user_name || `User ${e.user_id}`}</td>
                    <td style={{ padding: '8px 12px' }}>{e.field_revealed}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {REVEAL_LABEL[e.entity_type] || e.entity_type}
                      {e.entity_name ? ` · ${e.entity_name}` : ` #${e.entity_id}`}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{e.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '24px' }}>Admin Panel</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Manage users, roles, and system settings</p>
        </div>
        <button onClick={openNewUser} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          ➕ Add User
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {ROLES.map(r => {
          const count = users.filter(u => u.role === r.value).length;
          return (
            <div key={r.value} style={{ background: r.bg, border: `1px solid ${r.color}25`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 800, color: r.color }}>{count}</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: r.color, opacity: 0.85 }}>{r.label}</div>
            </div>
          );
        })}
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Team Members ({users.length})</h2>
          <button onClick={fetchServerStatus} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f8fafc', cursor: 'pointer', fontSize: '12px' }}>
            🖥️ Server Status
          </button>
        </div>

        {serverStatus && (
          <div style={{ padding: '12px 16px', background: '#f0fdf4', borderRadius: '8px', margin: '0 0 12px 0', fontSize: '13px', border: '1px solid #bbf7d0' }}>
            <strong>Server:</strong> {serverStatus.status} | <strong>Uptime:</strong> {Math.floor(serverStatus.uptime / 3600)}h {Math.floor((serverStatus.uptime % 3600) / 60)}m | <strong>Memory:</strong> {Math.round((serverStatus.memory?.used || 0) / 1024 / 1024)}MB / {Math.round((serverStatus.memory?.total || 0) / 1024 / 1024)}MB | <strong>CPU Load:</strong> {serverStatus.cpu?.[0]?.toFixed(2) || 'N/A'}
          </div>
        )}

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ minWidth: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? users.map(user => {
                const ri = getRoleInfo(user.role);
                const isRevoked = user.status === 'revoked';
                return (
                  <tr key={user.id} style={{ opacity: isRevoked ? 0.5 : 1 }}>
                    <td><strong>{user.name}</strong></td>
                    <td style={{ fontSize: '13px' }}>{user.email}</td>
                    <td style={{ fontSize: '13px' }}>{user.phone || '-'}</td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: ri.bg, color: ri.color, border: `1px solid ${ri.color}30` }}>
                        {ri.label}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${isRevoked ? 'danger' : 'success'}`} style={{ fontSize: '11px' }}>
                        {isRevoked ? '🚫 Revoked' : '✅ Active'}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px' }}>{user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleEdit(user)} title="Edit"
                          style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          ✏️ Edit
                        </button>
                        <button onClick={() => openResetPassword(user)} title="Reset Password"
                          style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #93c5fd', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          🔑 Password
                        </button>
                        <button onClick={() => handleRevoke(user)} title={isRevoked ? 'Activate' : 'Revoke'}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${isRevoked ? '#86efac' : '#fdba74'}`, background: isRevoked ? '#f0fdf4' : '#fff7ed', color: isRevoked ? '#16a34a' : '#ea580c', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          {isRevoked ? '✅ Activate' : '🚫 Revoke'}
                        </button>
                        <button onClick={() => handleDelete(user)} title="Delete"
                          style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No users found. Click "Add User" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Roles Reference */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2 className="card-title">Role Permissions Guide</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginTop: '12px' }}>
          {ROLES.map(r => (
            <div key={r.value} style={{ padding: '14px', background: r.bg, borderRadius: '10px', border: `1px solid ${r.color}20` }}>
              <div style={{ fontWeight: 700, color: r.color, fontSize: '14px', marginBottom: '4px' }}>{r.label}</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>{r.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingUser ? `Edit User — ${editingUser.name}` : 'Add New User'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Full Name *</label>
                  <input type="text" className="form-input" required value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Rajesh Mehta" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" required value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input type="text" className="form-input" value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="9820XXXXXX" />
                </div>
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-input" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <input type="password" className="form-input" value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? '(unchanged)' : 'Min 6 characters'}
                    required={!editingUser} minLength={editingUser ? 0 : 6} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && passwordUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3 className="modal-title">🔑 Reset Password — {passwordUser.name}</h3>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input type="password" className="form-input" required minLength={6} value={newPassword}
                  onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" autoFocus />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Reset Password</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <RevealTracker />
    </div>
  );
};

export default Admin;
